import { mapComponentType, mapObjectWithComponentType } from '../../functions/checklist'
import { ChecklistInstance, ChecklistSection, ChecklistSectionCheck } from '../../types/checklists'
import { NumCalcComponentLastValue } from '../../ui/components/widgets/NumberCalculatorComponent/NumberCalculatorComponent'
import { ChecklistEditAction } from '../actions/checklistEdit'

export interface ChecklistEditState {
  /**
   * The checklist instance obtained from the server.
   */
  checklist: ChecklistInstance | null
  /**
   * The error that occurred when fetching the checklist.
   */
  error: Error | null
  /**
   * Whether the checklist is being fetched.
   */
  fetching: boolean
  /**
   * Whether the checklist has been modified.
   */
  modified: boolean
  /**
   * Whether the checklist is being submitted.
   */
  submitting: boolean
  /**
   * The **filling** metadata of the {@link ChecklistInstance} components.
   *
   * Key is the component id.
   */
  componentsMetadata: Record<string, EditComponentMetadata>

  /**
   * Whether there is at least one component with an error.
   */
  someComponentsHasError: boolean

  /**
   * Whether there is at least one component with a validation error.
   */
  someComponentsHasValidationErrors: boolean

  /**
   * The photos being uploaded (or whose upload failed) to `file` and `multiFile` components.
   *
   * Key is the upload id.
   */
  pendingUploads: Record<string, PendingUpload>
}

export interface ChecklistComponentFileData {
  path: string
  blob?: Blob
}

export type PendingUploadPhase = 'queued' | 'uploading' | 'verifying' | 'failed'

export type PendingUploadErrorKind = 'timeout' | 'network' | 'exifTooOld' | 'exifMissing' | 'notImage' | 'server'

export interface PendingUpload {
  id: string
  componentId: string
  checkId: string
  file: File
  phase: PendingUploadPhase
  /**
   * Timestamp (ms) of when the upload started (baseline included), used to draw the progress bar.
   */
  startedAt?: number
  errorKind?: PendingUploadErrorKind
}

export type EditComponentMetadataValue =
  | Array<{ subComponentName: string; value: number | number[] }>
  | string
  | number
  | undefined
  | null
  | ChecklistComponentFileData
  | NumCalcComponentLastValue
  | ChecklistComponentFileData[]

export interface SimpleEditComponentMetadata {
  /**
   * The id of the component.
   */
  id: string
  /**
   * Whether the component must show {@link error}.
   */
  showError: boolean
  /**
   * The value of the component.
   */
  value?: EditComponentMetadataValue
  /**
   * Whether the component is required.
   */
  required: boolean
  /**
   * The error message.
   *
   * If the error is local, the slug is the slug of the error to be translated.
   * If the error is server, the message is the message of the error.
   */
  error: EditComponentMetadataError
  /**
   * Whether the component is being updated.
   */
  updating: boolean
  /**
   * Whether the component can not be updated.
   */
  disabled: boolean
}

interface TextEditComponentMetadata extends SimpleEditComponentMetadata {
  type: 'text'
  value?: string
}

interface FileEditComponentMetadata extends SimpleEditComponentMetadata {
  type: 'file'
  value?: ChecklistComponentFileData
}

interface MultiFileEditComponentMetadata extends SimpleEditComponentMetadata {
  type: 'multiFile'
  value?: ChecklistComponentFileData[]
}

interface NumberEditComponentMetadata extends SimpleEditComponentMetadata {
  type: 'number'
  value?: number
}

interface NumberCalculatorEditComponentMetadata extends SimpleEditComponentMetadata {
  type: 'numberCalculator'
  value?: NumCalcComponentLastValue
}

interface SelectEditComponentMetadata extends SimpleEditComponentMetadata {
  type: 'select'
  value?: string
}

interface AssetHoursEditComponentMetadata extends SimpleEditComponentMetadata {
  type: 'assetHours'
  value?: number
}

interface CalculatorEditComponentMetadata extends SimpleEditComponentMetadata {
  type: 'calculator'
  value?: number
}

type EditComponentMetadata =
  | TextEditComponentMetadata
  | FileEditComponentMetadata
  | MultiFileEditComponentMetadata
  | NumberEditComponentMetadata
  | NumberCalculatorEditComponentMetadata
  | SelectEditComponentMetadata
  | AssetHoursEditComponentMetadata
  | CalculatorEditComponentMetadata

export type EditComponentMetadataError =
  | { type: 'server'; message?: string }
  | { type: 'local'; slug: 'required' }
  | null

const editInitialState: ChecklistEditState = {
  checklist: null,
  error: null,
  fetching: false,
  modified: false,
  componentsMetadata: {},
  someComponentsHasError: false,
  someComponentsHasValidationErrors: false,
  submitting: false,
  pendingUploads: {},
}

export const checklistEdit = (state: ChecklistEditState = editInitialState, action: ChecklistEditAction) => {
  switch (action.type) {
    case 'persist/REHYDRATE':
      return { ...state }
    case 'CHECKLIST_EDIT_IS_FETCHING':
      return {
        ...state,
        error: null,
        fetching: true,
        modified: false,
        someComponentHasError: false,
        componentsMetadata: {},
      }
    case 'CHECKLIST_EDIT_FETCH_ERROR':
      return {
        ...state,
        error: action.payload,
        fetching: false,
        modified: false,
        someComponentHasError: false,
        componentsMetadata: {},
        pendingUploads: {},
      }
    case 'CHECKLIST_EDIT_FETCH_SUCCESS':
      const checklist = action.payload ?? state.checklist
      if (!checklist || 'id' in checklist === false) {
        return state
      }
      const componentsMetadata = _initChecklistMetadata(checklist as ChecklistInstance)

      return {
        ...state,
        checklist: checklist as ChecklistInstance,
        error: null,
        fetching: false,
        modified: false,
        componentsMetadata,
        someComponentsHasError: _hasSomeComponentError(componentsMetadata),
        someComponentsHasValidationErrors: _hasSomeComponentValidationError(componentsMetadata),
        // the checklist can be re-fetched while uploading (e.g. when a check is added): keep the uploads of the
        // components that still exist
        pendingUploads: _filterRecord(state.pendingUploads, upload => upload.componentId in componentsMetadata),
      }

    case 'CHECKLIST_EDIT_IS_MODIFIED':
      return {
        ...state,
        modified: action.payload,
      }

    case 'CHECKLIST_EDIT_IS_UPDATING_COMPONENT':
      return _onComponentUpdate(
        state,
        {
          componentId: action.payload,
          callback: metadataState => {
            return {
              ...metadataState,
              updating: true,
            }
          },
        },
        false
      )
    case 'CHECKLIST_EDIT_SET_COMPONENT_ERROR':
      return _onComponentUpdate(state, {
        componentId: action.payload.componentId,
        callback: metadataState => ({
          ...metadataState,
          updating: false,
          error: {
            type: 'server',
            message: action.payload.error,
          },
          showError: true,
        }),
      })
    case 'CHECKLIST_EDIT_SET_COMPONENT_UPDATED': {
      return _onComponentUpdate(state, {
        componentId: action.payload.componentId,
        callback: metadataState => {
          return {
            ...metadataState,
            updating: false,
            value: action.payload.value,
          }
        },
      })
    }
    case 'CHECKLIST_EDIT_SET_MULTI_FILE_COMPONENT_FILE_DELETED':
      return _onComponentUpdate(state, {
        componentId: action.payload.componentId,
        callback: metadataState => {
          const value = (metadataState.value as ChecklistComponentFileData[]).filter(
            file => file.path !== action.payload.fileDeleted
          )
          return {
            ...metadataState,
            value,
            updating: false,
          } as MultiFileEditComponentMetadata
        },
      })
    case 'CHECKLIST_EDIT_SET_COMPONENT_BLURRED':
      return _onComponentUpdate(state, {
        componentId: action.payload,
        callback: metadataState => ({
          ...metadataState,
          showError: true,
        }),
      })

    case 'CHECKLIST_EDIT_SHOW_COMPONENTS_ERRORS':
      return {
        ...state,
        componentsMetadata: {
          ...Object.entries(state.componentsMetadata).reduce((acc, [key, value]) => {
            return { ...acc, [key]: { ...value, showError: true } }
          }, {}),
        },
      }
    case 'CHECKLIST_EDIT_IS_SUBMITTING_CHECKLIST':
      return {
        ...state,
        submitting: true,
      }
    case 'CHECKLIST_EDIT_SUBMITTED_SUCCESSFULLY':
      return {
        ...state,
        submitting: false,
      }
    case 'CHECKLIST_EDIT_SUBMIT_CHECKLIST_ERROR':
      return {
        ...state,
        submitting: false,
        error: action.payload,
      }
    case 'CHECKLIST_EDIT_UPLOAD_QUEUED':
      return {
        ...state,
        pendingUploads: {
          ..._dropFailedUploadsOfFileComponent(state, action.payload.componentId),
          [action.payload.id]: action.payload,
        },
      }
    case 'CHECKLIST_EDIT_UPLOAD_STARTED':
      return _onPendingUploadUpdate(state, action.payload.uploadId, upload => ({
        ...upload,
        phase: 'uploading',
        startedAt: action.payload.startedAt,
      }))
    case 'CHECKLIST_EDIT_UPLOAD_VERIFYING':
      return _onPendingUploadUpdate(state, action.payload, upload => ({ ...upload, phase: 'verifying' }))
    case 'CHECKLIST_EDIT_UPLOAD_FAILED':
      return _onPendingUploadUpdate(state, action.payload.uploadId, upload => ({
        ...upload,
        phase: 'failed',
        errorKind: action.payload.errorKind,
      }))
    case 'CHECKLIST_EDIT_UPLOAD_SAVED': {
      const upload = state.pendingUploads[action.payload.uploadId]
      if (!upload) {
        return state
      }

      const pendingUploads = _filterRecord(
        _dropFailedUploadsOfFileComponent(state, upload.componentId),
        pending => pending.id !== upload.id
      )
      const savedFile: ChecklistComponentFileData = { path: action.payload.path, blob: upload.file }

      return {
        ..._onComponentUpdate(state, {
          componentId: upload.componentId,
          callback: metadataState => {
            if (metadataState.type === 'multiFile') {
              const files = metadataState.value ?? []
              // the file can already be there if the checklist has been re-fetched in the meantime
              if (files.some(file => file.path === savedFile.path)) {
                return metadataState
              }
              return { ...metadataState, value: [...files, savedFile] }
            }

            if (metadataState.type === 'file') {
              return { ...metadataState, value: savedFile }
            }

            return metadataState
          },
        }),
        pendingUploads,
      }
    }
    case 'CHECKLIST_EDIT_UPLOAD_RETRIED':
      return _onPendingUploadUpdate(state, action.payload, upload => ({
        ...upload,
        phase: 'queued',
        startedAt: undefined,
        errorKind: undefined,
      }))
    case 'CHECKLIST_EDIT_UPLOAD_DISMISSED':
      return {
        ...state,
        pendingUploads: _filterRecord(state.pendingUploads, upload => upload.id !== action.payload),
      }
    case 'CHECKLIST_EDIT_UPLOADS_CLEARED':
      return {
        ...state,
        pendingUploads: {},
      }
    default:
      return state
  }
}

/**
 * Whether there is at least one upload not yet concluded (queued, uploading or verifying).
 */
export function hasUploadsInProgress(state: ChecklistEditState): boolean {
  return Object.values(state.pendingUploads).some(upload => upload.phase !== 'failed')
}

function _onPendingUploadUpdate(
  state: ChecklistEditState,
  uploadId: string,
  callback: (upload: PendingUpload) => PendingUpload
): ChecklistEditState {
  const upload = state.pendingUploads[uploadId]
  if (!upload) {
    return state
  }

  return {
    ...state,
    pendingUploads: { ...state.pendingUploads, [uploadId]: callback(upload) },
  }
}

/**
 * The pending uploads without the failed ones of the component with id {@link componentId}, if it is a 'file'
 * component: it holds a single photo, so a new photo replaces the failed ones, that would otherwise hide it.
 */
function _dropFailedUploadsOfFileComponent(
  state: ChecklistEditState,
  componentId: string
): Record<string, PendingUpload> {
  if (state.componentsMetadata[componentId]?.type !== 'file') {
    return state.pendingUploads
  }

  return _filterRecord(
    state.pendingUploads,
    pending => pending.componentId !== componentId || pending.phase !== 'failed'
  )
}

function _filterRecord<T>(record: Record<string, T>, predicate: (value: T) => boolean): Record<string, T> {
  return Object.entries(record).reduce<Record<string, T>>((acc, [key, value]) => {
    if (predicate(value)) {
      acc[key] = value
    }
    return acc
  }, {})
}

/**
 * Whether there is at least one component with an error ({@link EditComponentMetadata.error}).
 */
function _hasSomeComponentError(componentsMetadata: Record<string, EditComponentMetadata>): boolean {
  return Object.values(componentsMetadata).some(component => component.error)
}

/**
 * Whether there is at least one component with a validation error ({@link EditComponentMetadata.error}).
 */
function _hasSomeComponentValidationError(componentsMetadata: Record<string, EditComponentMetadata>): boolean {
  return Object.values(componentsMetadata).some(component => component.error?.type === 'local')
}
/**
 * Update the component metadata.
 *
 * Then update:
 * - {@link EditComponentMetadata} validation errors
 * - {@link ChecklistEditState.someComponentsHasError}
 * - {@link ChecklistEditState.someComponentsHasValidationErrors}
 *
 * @param state - The state of the checklist.
 */
function _onComponentUpdate(
  state: ChecklistEditState,
  options: { componentId: string; callback: (state: EditComponentMetadata) => EditComponentMetadata },
  validate: boolean = true
): ChecklistEditState {
  const componentMetadata = state.componentsMetadata[options.componentId]
  if (!componentMetadata) {
    return state
  }

  const updated = options.callback(componentMetadata)

  if (validate) {
    const serverError = updated.error?.type === 'server' ? updated.error : null
    const error = _validateComponentLocally(updated) ?? serverError
    updated.error = error
  }

  const newComponentMetadata = {
    ...state.componentsMetadata,
    [options.componentId]: updated,
  }

  return {
    ...state,
    componentsMetadata: newComponentMetadata,
    someComponentsHasError: _hasSomeComponentError(newComponentMetadata),
    someComponentsHasValidationErrors: _hasSomeComponentValidationError(newComponentMetadata),
  }
}

const _hasValue = <T>(value?: T | undefined | null): value is T => {
  return value !== undefined && value !== null
}
const _hasNoValue = <T>(value?: T | undefined | null): value is undefined | null => {
  return !_hasValue(value)
}

/**
 * Checklist WEB validation
 */
function _validateComponentLocally(component: EditComponentMetadata): EditComponentMetadataError {
  const requiredError: EditComponentMetadataError = { type: 'local', slug: 'required' }

  return mapObjectWithComponentType<EditComponentMetadata, EditComponentMetadataError>(component, {
    text: textComponent => {
      if (textComponent.required && (_hasNoValue(textComponent.value) || textComponent.value.trim().length === 0)) {
        return requiredError
      }
      return null
    },
    file: fileComponent => {
      if (
        fileComponent.required &&
        (_hasNoValue(fileComponent.value) || fileComponent.value?.path.trim().length === 0)
      ) {
        return requiredError
      }
      return null
    },
    multiFile: multiFileComponent => {
      if (
        multiFileComponent.required &&
        (_hasNoValue(multiFileComponent.value) || multiFileComponent.value?.length === 0)
      ) {
        return requiredError
      }
      return null
    },
    number: numberComponent => {
      if (numberComponent.required && _hasNoValue(numberComponent.value)) {
        return requiredError
      }
      return null
    },
    numberCalculator: numberCalculatorComponent => {
      if (numberCalculatorComponent.required && _hasNoValue(numberCalculatorComponent.value)) {
        return requiredError
      }
      return null
    },
    select: selectComponent => {
      if (selectComponent.required && _hasNoValue(selectComponent.value)) {
        return requiredError
      }
      return null
    },
    assetHours: assetHoursComponent => {
      if (assetHoursComponent.required && _hasNoValue(assetHoursComponent.value)) {
        return requiredError
      }
      return null
    },
    calculator: calculatorComponent => {
      if (calculatorComponent.required && _hasNoValue(calculatorComponent.value)) {
        return requiredError
      }
      return null
    },
    onElse: () => null,
  })
}

function _initChecklistMetadata(checklist: ChecklistInstance): Record<string, EditComponentMetadata> {
  return (
    checklist.sections?.reduce((acc, section) => {
      return { ...acc, ..._initSectionMetadata(section, { disabled: checklist.isConcluded() }) }
    }, {}) ?? {}
  )
}

function _initSectionMetadata(section: ChecklistSection, options: { disabled: boolean }) {
  return section.checks.reduce((acc, check) => {
    return { ...acc, ..._initCheckMetadata(check, options) }
  }, {})
}

function _initCheckMetadata(
  check: ChecklistSectionCheck,
  options: { disabled: boolean }
): Record<string, EditComponentMetadata> {
  return check.components.reduce((acc, component) => {
    // don't know why is nullable
    if (!component.id) {
      return acc
    }

    const value = mapComponentType<EditComponentMetadata['value']>(component.type.type, {
      file: () => {
        const filePath = (component.lastValue ?? component.defaultValue) as string
        return { path: filePath }
      },
      multiFile: () => {
        const files = (component.lastValue ?? component.defaultValue) as Array<{ file: string }>

        return (
          files?.map(file => ({
            path: file.file,
          })) ?? []
        )
      },
      onElse: () => (component.lastValue ?? component.defaultValue) as any,
    })

    const state: EditComponentMetadata = {
      type: component.type.type as EditComponentMetadata['type'],
      id: component.id,
      showError: false,
      error: null,
      value: value as any,
      required: component.required ?? false,
      updating: false,
      disabled: options.disabled,
    }

    const error = _validateComponentLocally(state)

    return {
      ...acc,
      [component.id]: { ...state, error },
    }
  }, {})
}
